// QUÉ CAMBIÓ en un catálogo de etiquetas o en sus colores — para mandar SOLO eso.
//
// Melanie Berrios (Pomares Insurance Group), 2026-10-29: *"se nos están
// borrando varias etiquetas que hemos creado para clasificar a los clientes"*.
//
// El catálogo de etiquetas (y el de colores, y los dos de pólizas) se guardaba
// ENTERO con la copia que tenía cada pestaña al abrirse. En una agencia con
// varias personas, quien tenía Deskte abierto desde temprano y creaba una
// etiqueta mandaba su lista vieja y borraba las que los demás habían creado
// mientras tanto. Es el fallo de E-391 (las columnas de Marilyn) y la regla 282:
// guardar una lista que edita el equipo es aplicar cambios, no reemplazarla.
//
// Así que la pantalla compara lo último que supo del servidor con lo que hay
// ahora en pantalla, y manda la diferencia. El servidor la aplica sobre lo que
// tiene guardado (deskte-api/src/utils/catalogoDeEtiquetas.ts).

const clave = (s) => String(s ?? '').trim().toLowerCase();

/** Catálogo (lista de nombres): qué se añadió y qué se quitó. Sin mirar mayúsculas. */
export function cambiosDeCatalogo(antes, despues) {
  const a = Array.isArray(antes) ? antes : [];
  const d = Array.isArray(despues) ? despues : [];
  const enAntes = new Set(a.map(clave));
  const enDespues = new Set(d.map(clave));
  const anadir = d.filter((t) => clave(t) && !enAntes.has(clave(t)));
  const quitar = a.filter((t) => clave(t) && !enDespues.has(clave(t)));
  return { anadir, quitar };
}

/** Colores (mapa nombre → color): qué se puso o cambió y qué se quitó. */
export function cambiosDeColores(antes, despues) {
  const a = antes && typeof antes === 'object' && !Array.isArray(antes) ? antes : {};
  const d = despues && typeof despues === 'object' && !Array.isArray(despues) ? despues : {};
  const poner = {};
  for (const [k, v] of Object.entries(d)) if (a[k] !== v) poner[k] = v;
  const quitar = Object.keys(a).filter((k) => !(k in d));
  return { poner, quitar };
}

/** ¿Hay algo que mandar? */
export function hayCambios(c) {
  if (!c) return false;
  if (Array.isArray(c.anadir) && c.anadir.length) return true;
  if (Array.isArray(c.quitar) && c.quitar.length) return true;
  if (Array.isArray(c.poner) ? c.poner.length : (c.poner && Object.keys(c.poner).length)) return true;
  return false;
}

/** Aplica unos cambios a un catálogo (sin repetir, sin mirar mayúsculas). */
export function aplicarAlCatalogo(lista, cambios) {
  const fuera = new Set((cambios?.quitar || []).map(clave));
  const out = [];
  const vistas = new Set();
  for (const t of [...(Array.isArray(lista) ? lista : []), ...(cambios?.anadir || [])]) {
    const k = clave(t);
    if (!k || fuera.has(k) || vistas.has(k)) continue;
    vistas.add(k);
    out.push(t);
  }
  return out;
}

/** Aplica unos cambios a un mapa de colores. */
export function aplicarALosColores(mapa, cambios) {
  const out = { ...(mapa && typeof mapa === 'object' && !Array.isArray(mapa) ? mapa : {}) };
  for (const k of cambios?.quitar || []) delete out[k];
  return { ...out, ...(cambios?.poner || {}) };
}

/**
 * Lo que queda en pantalla cuando el servidor contesta.
 *
 * El servidor devuelve el catálogo de TODA la agencia (con lo que hayan creado
 * los demás). Pero mientras viajaba la petición, la persona pudo tocar algo más:
 * eso se vuelve a aplicar encima, para no perderlo, y saldrá en el siguiente envío.
 */
export function fusionarRespuesta(tipo, servidor, enviado, ahora) {
  if (tipo === 'colores') return aplicarALosColores(servidor, cambiosDeColores(enviado, ahora));
  if (tipo === 'colaboradores') return aplicarAColaboradores(servidor, cambiosDeColaboradores(enviado, ahora));
  return aplicarAlCatalogo(servidor, cambiosDeCatalogo(enviado, ahora));
}

// ── AGENTES COLABORADORES DE LA AGENCIA (mismo fallo, misma regla) ──────────
// Cada colaborador lleva un `id` (el servidor se lo da a los que ya existían:
// deskte-api/src/utils/colaboradores.ts). Se manda `{ poner, quitar }`:
// los nuevos o editados, y los ids que se quitaron.

const igualColaborador = (a, b) =>
  (a?.name || '') === (b?.name || '') && (a?.lastName || '') === (b?.lastName || '') && (a?.npn || '') === (b?.npn || '');

/** Un id nuevo para un colaborador que se acaba de añadir en pantalla. */
export function idDeColaboradorNuevo() {
  return `c_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

export function cambiosDeColaboradores(antes, despues) {
  const a = (Array.isArray(antes) ? antes : []).filter((c) => c && c.id);
  const d = (Array.isArray(despues) ? despues : []).filter((c) => c && c.id);
  const porId = new Map(a.map((c) => [c.id, c]));
  const ids = new Set(d.map((c) => c.id));
  const poner = d.filter((c) => !porId.has(c.id) || !igualColaborador(porId.get(c.id), c));
  const quitar = a.filter((c) => !ids.has(c.id)).map((c) => c.id);
  return { anadir: [], poner, quitar };
}

export function aplicarAColaboradores(lista, cambios) {
  const fuera = new Set(cambios?.quitar || []);
  const out = (Array.isArray(lista) ? lista : []).filter((c) => !fuera.has(c?.id));
  for (const c of cambios?.poner || []) {
    const i = out.findIndex((x) => x?.id === c.id);
    if (i >= 0) out[i] = c; else out.push(c);
  }
  return out;
}
